package ui.app.assign.detail.definition

import androidx.compose.animation.*
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import api.ResourceOrientation
import api.ResourceType
import compose.assets.Svg
import compose.assets.svg.Calendar
import compose.assets.svg.Edit
import compose.assets.svg.Format_text
import compose.assets.svg.Text_long
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus
import org.jetbrains.compose.resources.StringResource
import org.ssoft.common.tools.nowDate
import org.ssoft.common.type.Mask
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.Navigation
import tools.*
import ui.app.assign.detail.AppDetailScope
import ui.resource.ResourceListingArgs
import ui.widget.*
import kotlin.math.roundToLong
import kotlin.time.Clock
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds

@Composable
fun AppDetailScope.DefaultFields(
    nnvl: AppDetailScope.Wrapper,
    modifier: Modifier = Modifier.width(256.dp),
    mainContent: @Composable () -> Unit = {},
    secondaryContent: @Composable () -> Unit = {}
) {
    NameField(nnvl, modifier)
    Space(16.dp)
    mainContent()
    LogoField(nnvl, modifier)
    Space(16.dp)
    SchedulerField(nnvl, modifier)
    Space(16.dp)
    secondaryContent()
    NoteField(nnvl, modifier)
}


@Composable
fun AppDetailScope.ImagePicker(
    field: EFieldRegularLong,
    label: StringResource,
    wrapper: AppDetailScope.Wrapper,
    modifier: Modifier = Modifier.width(256.dp),
    extraKey: String? = null,
    restrictionOrientation: Int = ResourceOrientation.UNDEFINED.mask
) {

    val imagePickerKey = remember(args.appId) {
        "${field.fieldName}:${args.appId}:$extraKey"
    }

    GlobalObjectShare.Consumer(imagePickerKey)

    Card(modifier) {
        Box {
            Column(Modifier.padding(16.dp)) {
                Text(label.rememberString())
                Space(8.dp)
                Card(Modifier.fillMaxWidth()) {
                    LazyImage(
                        id = field.state,
                        modifier = Modifier.fillMaxWidth(),
                        contentScale = ContentScale.FillWidth
                    )

                }
            }
            if (wrapper.canEdit) {
                FloatingActionButton(
                    onClick = {
                        GlobalObjectShare.launchImagePicker(
                            key = imagePickerKey,
                            nav = nav,
                            args = ResourceListingArgs(
                                companyId = args.companyId,
                                restrictionType = ResourceType.IMAGE.mask,
                                restrictionCanIgnoreAspectRatio = true,
                                restrictionOrientation = restrictionOrientation
                            )
                        ) {
                            it.firstOrNull()?.also { item ->
                                field.set(item.id)
                            }
                        }
                    },
                    modifier = Modifier.align(Alignment.BottomEnd)
                ) {
                    Icon(
                        imageVector = Svg.Edit,
                        contentDescription = null
                    )
                }
            }
        }

    }
}


@Composable
fun AppDetailScope.NameField(nnvl: AppDetailScope.Wrapper, modifier: Modifier) {
    Card(
        modifier = modifier
    ) {
        Column(
            Modifier.padding(horizontal = 16.dp)
                .padding(bottom = 16.dp, top = 8.dp)
        ) {
            TextField(
                readOnly = !nnvl.canEdit,
                value = nnvl.name.text,
                isError = nnvl.name.err,
                onValueChange = { nnvl.name.set(it) },
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = { Icon(Svg.Format_text, "") },
                label = { Text(Res.string.layer_bottom_editor__label_name.rememberString()) })
        }
    }
}

@Composable
fun AppDetailScope.NoteField(nnvl: AppDetailScope.Wrapper, modifier: Modifier = Modifier.width(256.dp)) {
    Card(
        modifier = modifier
    ) {
        Column(
            Modifier.padding(horizontal = 16.dp)
                .padding(bottom = 16.dp, top = 8.dp)
        ) {
            TextField(
                readOnly = !nnvl.canEdit,
                value = nnvl.note.text,
                isError = nnvl.note.err,
                onValueChange = { nnvl.note.set(it) },
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = { Icon(Svg.Text_long, "") },
                label = { Text(Res.string.layer_bottom_editor__label_note.rememberString()) })
        }
    }
}

@Composable
fun AppDetailScope.SchedulerField(nnvl: AppDetailScope.Wrapper, modifier: Modifier = Modifier.width(256.dp)) {
    AppSchedulerField(
        canEdit = nnvl.canEdit,
        scheduled = nnvl.scheduled,
        start = nnvl.start,
        end = nnvl.end,
        modifier = modifier
    )
}


@Composable
fun AppSchedulerField(
    canEdit: Boolean,
    scheduled: EFieldRegularBoolean,
    start: EFieldRegularDate,
    end: EFieldRegularDate,
    enabledRange: Pair<LocalDate, LocalDate> = Clock.nowDate() to Clock.nowDate().plus(DatePeriod(days = 364)),
    modifier: Modifier = Modifier.width(256.dp)
) {
    val stateDate = DialogState.remember()
    DateRangePickerDialog(
        state = stateDate,
        start = start,
        end = end,
        enabledRange = enabledRange
    )

    Card(
        modifier = modifier,
        shape = RoundedCornerShape(4.dp, 4.dp)
    ) {
        Column(
            Modifier.fillMaxWidth()
        ) {
            Text(
                text = Res.string.layer_bottom_editor__label_visibility_scheduler.rememberString(),
                style = MaterialTheme.typography.labelLarge,
                modifier = Modifier.padding(8.dp)
            )
            Card(
                colors = CardDefaults.cardColors(MaterialTheme.colorScheme.inverseSurface),
                shape = RoundedCornerShape(4.dp, 4.dp)
            ) {
                Row(
                    Modifier
                        .fillMaxWidth()
                        .clip(CardDefaults.shape)
                        .clickable(canEdit) {
                            scheduled.set(!scheduled.state)
                            if (scheduled.state) stateDate.show()
                        }
                        .padding(8.dp)) {
                    Icon(
                        imageVector = Svg.Calendar,
                        contentDescription = null,
                        modifier = Modifier.align(Alignment.CenterVertically)
                    )
                    Space(8.dp)
                    Text(
                        text = Res.string.layer_bottom_editor__label_visibility_scheduler_enable.rememberString(),
                        modifier = Modifier
                            .weight(1F)
                            .align(Alignment.CenterVertically)
                    )
                    Space(8.dp)
                    IconicSwitch(
                        checked = scheduled.state,
                        onCheckedChange = {
                            scheduled.set(it)
                            if (it) stateDate.show()

                        },
                        modifier = Modifier.align(Alignment.CenterVertically)
                    )
                }
            }

            AnimatedVisibility(
                scheduled.state,
                enter = fadeIn() + expandVertically(),
                exit = fadeOut() + shrinkVertically()
            ) {
                Column(Modifier.fillMaxWidth()) {
                    Space(8.dp)
                    Surface(
                        color = MaterialTheme.colorScheme.inverseSurface,
                        contentColor = MaterialTheme.colorScheme.inverseOnSurface
                    ) {
                        Column(
                            Modifier.fillMaxWidth()
                                .clip(CardDefaults.shape)
                                .clickable(canEdit) { stateDate.show() }) {
                            Text(
                                text = "${start.state} <> ${end.state}",
                                modifier = Modifier.align(Alignment.CenterHorizontally)
                                    .padding(8.dp)
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun AppDetailScope.LogoField(nnvl: AppDetailScope.Wrapper, modifier: Modifier = Modifier.width(256.dp)) {
    Card(modifier) {
        AppLogoField(
            canEdit = nnvl.canEdit,
            appId = args.appId,
            companyId = args.companyId,
            nav = nav,
            logo = nnvl.logo
        )
    }
}


@Composable
fun AppLogoField(
    canEdit: Boolean,
    appId: Long,
    companyId: Long,
    nav: Navigation,
    logo: EFieldRegularLong,
    label: StringResource = Res.string.layer_bottom_detail__pick_app_icon_label,
    size: Dp = 192.dp,
    modifier: Modifier = Modifier,
) {

    val imagePickerKey = remember(appId) {
        "LogoField:${appId}"
    }

    GlobalObjectShare.Consumer(imagePickerKey)

    fun openResourcePicker() {
        GlobalObjectShare.launchImagePicker(
            key = imagePickerKey,
            nav = nav,
            args = ResourceListingArgs(
                companyId = companyId,
                restrictionType = ResourceType.IMAGE.mask,
                restrictionCanIgnoreAspectRatio = true
            )
        ) {
            it.firstOrNull()?.apply {
                logo.set(id)
            }
        }
    }

    fun openIconPicker() {
        GlobalObjectShare.launchIconPicker(
            key = imagePickerKey,
            nav = nav
        ) {
            logo.set(it.id)
        }
    }

    Box(modifier = modifier) {
        Card(shape = RoundedCornerShape(4.dp, 4.dp)) {
            Column {
                Text(
                    text = label.rememberString(),
                    style = MaterialTheme.typography.labelLarge,
                    modifier = Modifier.padding(8.dp)
                )
                Card(
                    modifier = Modifier.size(size),
                    shape = RoundedCornerShape(4.dp, 4.dp)
                ) {
                    LazyImage(
                        id = logo.state,
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.FillWidth
                    )
                }
            }
        }
        if (canEdit) {
            Box(Modifier.align(Alignment.BottomEnd)) {
                var showDropDown by remember { mutableStateOf(false) }
                DropdownMenu(
                    expanded = showDropDown,
                    onDismissRequest = { showDropDown = false }) {
                    DropdownMenuItem(
                        text = { Text(Res.string.layer_bottom_app_logo_icon.rememberString()) },
                        onClick = {
                            openIconPicker()
                            showDropDown = false
                        })
                    DropdownMenuItem(
                        text = { Text(Res.string.layer_bottom_app_logo_image.rememberString()) },
                        onClick = {
                            openResourcePicker()
                            showDropDown = false
                        })
                }
                if (size < 192.dp) {
                    SmallFloatingActionButton(onClick = { showDropDown = true }) {
                        Icon(
                            imageVector = Svg.Edit,
                            contentDescription = null
                        )
                    }
                } else {
                    FloatingActionButton(
                        onClick = { showDropDown = true },
                        modifier = Modifier.padding(4.dp)
                    ) {
                        Icon(
                            imageVector = Svg.Edit,
                            contentDescription = null
                        )
                    }
                }
            }
        }
    }
}


@Composable
fun AppDetailScope.FieldTextString(
    icon: ImageVector,
    field: EFieldTextString,
    label: StringResource,
    canEdit: Boolean
) {
    Card(
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(
            Modifier.padding(horizontal = 16.dp)
                .padding(bottom = 16.dp, top = 8.dp)
        ) {
            TextField(
                readOnly = !canEdit,
                value = field.text,
                isError = field.err,
                onValueChange = { field.set(it) },
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = { Icon(icon, "") },
                label = { Text(label.rememberString()) })
        }
    }
}

@Composable
fun AppDetailScope.FieldTextInt(
    icon: ImageVector,
    field: EFieldTextInt,
    label: StringResource,
    canEdit: Boolean
) {
    Card(
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(
            Modifier.padding(horizontal = 16.dp)
                .padding(bottom = 16.dp, top = 8.dp)
        ) {
            TextField(
                readOnly = !canEdit,
                value = field.text,
                isError = field.err,
                onValueChange = { field.set(it) },
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = { Icon(icon, "") },
                label = { Text(label.rememberString()) })
        }
    }
}


@Composable
fun TimersSettings(
    timerSplash: EFieldDualLongAsFloat,
    timerGame: EFieldDualLongAsFloat,
    timerResult: EFieldDualLongAsFloat,
    stepsSplash: Int = 98,
    stepsGame: Int = 6,
    stepsResult: Int = 98,
) {
    Card(
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(16.dp),
            modifier = Modifier.align(Alignment.CenterHorizontally)
                .padding(16.dp)
        ) {
            Text(
                text = Res.string.layer_bottom_wheel_page_activation_timers.rememberString(),
                style = MaterialTheme.typography.titleLarge,
                modifier = Modifier.fillMaxWidth(),
                textAlign = TextAlign.Center
            )

            FieldTimer(
                label = Res.string.layer_bottom_wheel_page_activation_timers_splash,
                field = timerSplash,
                steps = stepsSplash,
                range = 0.01f..1f,
                modifier = Modifier.fillMaxWidth()
            )

            FieldTimer(
                label = Res.string.layer_bottom_wheel_page_activation_timers_game,
                field = timerGame,
                steps = stepsGame,
                range = 0.20f..1f,
                modifier = Modifier.fillMaxWidth()
            )

            FieldTimer(
                label = Res.string.layer_bottom_wheel_page_activation_timers_result,
                field = timerResult,
                steps = stepsResult,
                range = 0.01f..1f,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@Composable
fun FieldTimer(
    label: StringResource,
    field: EFieldDualLongAsFloat,
    canEdit: Boolean = true,
    modifier: Modifier = Modifier,
    steps: Int = 0,
    range: ClosedFloatingPointRange<Float> = 0.01f..1f
) {
    Card(modifier) {
        Text(
            text = label.rememberString(),
            style = MaterialTheme.typography.labelLarge,
            modifier = Modifier.padding(vertical = 8.dp)
                .padding(horizontal = 16.dp)
        )
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Text(
                (field.state * field.maxValue.toFloat())
                    .roundToLong()
                    .milliseconds
                    .inWholeSeconds
                    .seconds
                    .toString(),
                modifier = Modifier.width(64.dp)
                    .align(Alignment.CenterVertically)
            )
            Slider(
                value = field.state,
                enabled = canEdit,
                valueRange = range,
                modifier = Modifier.weight(1F),
                steps = steps,
                colors = SliderDefaults.colors(
                    activeTickColor = Color.Transparent,
                    inactiveTickColor = Color.Transparent,
                ),
                onValueChange = {
                    field.set(it)
                })
        }
    }
}

@Composable
fun AppDetailScope.CheckboxGroup(
    label: StringResource,
    modifier: Modifier = Modifier.width(256.dp),
    body: @Composable ColumnScope.() -> Unit
) {
    Card(modifier = modifier) {
        Column(
            Modifier.padding(16.dp)
                .fillMaxWidth()
        ) {
            Text(label.rememberString())
            Space(8.dp)
            Card(colors = CardDefaults.cardColors(MaterialTheme.colorScheme.inverseSurface)) {
                Column(Modifier.fillMaxWidth()) {
                    body()
                }
            }
        }
    }
}

@Composable
fun AppDetailScope.CheckboxRow(
    wrapper: AppDetailScope.Wrapper,
    field: EFieldRegularBoolean,
    icon: ImageVector,
    label: StringResource
) {
    Row(
        Modifier
            .fillMaxWidth()
            .clip(CardDefaults.shape)
            .clickable(wrapper.canEdit) { field.set(!field.state) }
            .padding(8.dp)) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            modifier = Modifier.align(Alignment.CenterVertically)
        )
        Space(8.dp)
        Text(
            text = label.rememberString(),
            modifier = Modifier
                .weight(1F)
                .align(Alignment.CenterVertically)
        )
        Space(8.dp)
        IconicSwitch(
            checked = field.state,
            onCheckedChange = { field.set(it) },
            modifier = Modifier.align(Alignment.CenterVertically)
        )
    }
}


@Composable
fun AppDetailScope.RadioRow(
    wrapper: AppDetailScope.Wrapper,
    field: EFieldRegularInt,
    mask: Mask,
    label: StringResource
) {
    Row(
        Modifier
            .fillMaxWidth()
            .clip(CardDefaults.shape)
            .clickable(wrapper.canEdit) { field.set(mask.mask) }
            .padding(8.dp)) {
        RadioButton(
            selected = mask match field.state,
            onClick = { field.set(mask.mask) },
            colors = RadioButtonDefaults.colors(
                selectedColor = MaterialTheme.colorScheme.primaryContainer
            ),
            modifier = Modifier.align(Alignment.CenterVertically)
        )
        Space(8.dp)
        Text(
            text = label.rememberString(),
            modifier = Modifier
                .weight(1F)
                .align(Alignment.CenterVertically)
        )

    }
}


@Composable
fun AppDetailScope.DropDownField(
    icon: ImageVector,
    field: EFieldRegularInt,
    label: StringResource,
    variants: Map<Int, StringResource>,
    modifier: Modifier,
    canEdit: Boolean
) {
    var isExpanded by remember {
        mutableStateOf(false)
    }
    Card(modifier = modifier) {
        Column(
            Modifier.padding(16.dp)
                .fillMaxWidth()
        ) {
            Text(label.rememberString())
            Space(8.dp)
            Card(
                enabled = canEdit,
                onClick = { isExpanded = !isExpanded },
                colors = CardDefaults.cardColors(MaterialTheme.colorScheme.inverseSurface)
            ) {
                Row(
                    Modifier.padding(16.dp)
                        .fillMaxWidth()
                ) {
                    Icon(icon, null)
                    Space(8.dp)
                    Text(text = variants[field.state]?.rememberString() ?: "")
                }

                DropdownMenu(
                    expanded = isExpanded,
                    onDismissRequest = { isExpanded = false },
                ) {
                    variants.entries.forEach { entry ->
                        DropdownMenuItem(
                            text = { Text(entry.value.rememberString()) },
                            onClick = {
                                field.set(entry.key)
                                isExpanded = false
                            })

                    }
                }
            }
        }
    }
}